import { CdkObserveContent } from '@angular/cdk/observers';
import { DatePipe, JsonPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  linkedSignal,
  model,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  ChipComponent,
  ClickOutsideDirective,
  IconComponent,
  InPipe,
  InputComponent,
  modalFn,
  RepeatDirective,
} from '@softline/ui-core';
import { BookmarkRepository } from '../../repository/bookmark.repository';
import { LastSearchBookmarkService } from '../../services/last-search-bookmark.service';

@Component({
  selector: 'soft-entity-search-input',
  templateUrl: './entity-search-input.component.html',
  styleUrls: ['./entity-search-input.component.css'],
  imports: [
    InputComponent,
    DatePipe,
    CdkObserveContent,
    ClickOutsideDirective,
    ChipComponent,
    JsonPipe,
    RepeatDirective,
    IconComponent,
    InPipe,
    NgTemplateOutlet,
  ],
  providers: [
    //TODO: Remove
    {
      provide: LastSearchBookmarkService,
      useFactory: () => new LastSearchBookmarkService(),
    },
    {
      provide: BookmarkRepository,
      useFactory: () =>
        new BookmarkRepository({ category: 'last-search', subCategory: 'product' }),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntitySearchInputComponent<T> {
  private readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');
  private readonly modal = modalFn();

  public readonly selectedObject = model<any | null>(null);

  public readonly searchFn =
    input.required<(searchTerm: string | undefined | null) => void>();

  public readonly loading = model<boolean>(false);
  public readonly value = model<string | undefined | null>();
  public readonly _value = linkedSignal<string | undefined | null>(() => this.value());

  public readonly simplified = input<boolean>(false);
  public readonly label = input<string>('Soft Entity Search Input');
  public readonly required = input<boolean>(false);
  public readonly icon = input<string>('fa-regular fa-database');
  public readonly placeholder = input<string>('Suchbegriff eingeben...');

  public readonly resource = input(); //TODO:
  public readonly searchResults = input<any[] | null | undefined>(null);

  public readonly resultTemplate = input.required<
    TemplateRef<{
      $implicit: any;
    }>
  >();
  public readonly suggestions = input<boolean>(false);

  private readonly lastSearchBookmarkService = inject(LastSearchBookmarkService);
  private readonly lastEntityBookmarkService = inject(LastSearchBookmarkService); //TODO:
  private readonly favoriteEntityBookmarkService = inject(LastSearchBookmarkService); //TODO:

  public readonly sortedLastSearchResults = computed(() => {
    const last = this.lastSearchBookmarkService.all() ?? [];
    return last?.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  });
  public readonly sortedLastUsedEntities = linkedSignal<any[]>(() => []); //TODO:
  public readonly favoriteEntities = linkedSignal<any[]>(() => []); //TODO:

  public readonly searching = linkedSignal(() => this.loading());
  public readonly searched = signal(false);
  public readonly searchFocused = signal(false);

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event) {
    if (!this.searchFocused()) {
      return;
    }
    this.inputElement()?.nativeElement.blur();
    this.loading.set(false);
    this.searchFocused.set(false);
    this._value.set('');
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnter(event: Event) {
    if (!this.searchFocused()) {
      return;
    }
    //this.search();
  }

  @HostListener('document:keydown.tab', ['$event'])
  onTab(event: Event) {
    if (!this.searchFocused()) {
      return;
    }
    //this.search();
  }

  @HostListener('document:keydown.f4', ['$event'])
  onF4(event: Event) {
    if (!this.searchFocused()) {
      return;
    }
    this.openAdvancedSearch();
  }

  public readonly search = (): void => {
    const searchInputValue = this._value();
    if (searchInputValue) this.lastSearchBookmarkService.search(searchInputValue);
    this.searchFn()?.(searchInputValue);
    this.value.set(searchInputValue);
    this.searched.set(true);
    this.searching.set(false);
    this.loading.set(false);
  };

  public readonly deleteAllLastSearchResults = (): void => {
    this.lastSearchBookmarkService.deleteAll();
  };

  public readonly deleteLastSearchResult = (key: string): void => {
    this.lastSearchBookmarkService.delete(key);
  };

  //TODO: move TO SERVICE
  public readonly setFavorite = (entity: any) => {
    if (this.favoriteEntities().includes(entity)) {
      this.favoriteEntities().splice(this.favoriteEntities().indexOf(entity), 1);
      return;
    }
    this.favoriteEntities().push(entity);
  };

  //TODO: move TO SERVICE
  public readonly setLastUsed = (entity: any) => {
    if (this.sortedLastUsedEntities().includes(entity)) {
      return;
    }
    this.sortedLastUsedEntities().push(entity);
  };

  public readonly setFocused = () => {
    this.searchFocused.set(true);
    this.inputElement()?.nativeElement.focus();
  };

  public readonly setInputValue = (value: string | null | undefined) => {
    this._value.set(value);
  };

  private debounceHandle: ReturnType<typeof setTimeout> | null = null;

  public readonly scheduleSearch = () => {
    this.searching.set(true);
    this.loading.set(true);

    if (this.debounceHandle) {
      clearTimeout(this.debounceHandle);
      this.debounceHandle = null;
    }

    const currentValue = this._value();

    if (!currentValue) {
      this.loading.set(false);
      this.searching.set(false);
      this.search();
      return;
    }

    this.debounceHandle = setTimeout(() => {
      this.search();
      this.debounceHandle = null;
    }, 300);
  };

  public readonly onInputChange = (value: string | null | undefined) => {
    this.setInputValue(value);
    this.searched.set(false);
    this.scheduleSearch();
  };

  public readonly openAdvancedSearch = () => {
    //TODO: open modal
    this.modal({
      title: 'Advanced Search Modal',
    });
  };
}
